/*
  Minimal QR encoder - byte mode, error correction level L, versions 1 to 5.
  That covers 106 bytes, far more than a plain product URL needs, and every
  one of those versions uses a single error-correction block, so no
  interleaving is required. Version 6 would need two blocks, and 7 and up a
  version-information block; capping at 5 keeps this small enough to check.
*/
var neferQrSvg = (function () {
  'use strict';

  // GF(256), primitive polynomial 0x11D.
  var EXP = new Array(512);
  var LOG = new Array(256);
  (function () {
    var x = 1;
    for (var i = 0; i < 255; i++) {
      EXP[i] = x;
      LOG[x] = i;
      x <<= 1;
      if (x & 0x100) x ^= 0x11d;
    }
    for (var j = 255; j < 512; j++) EXP[j] = EXP[j - 255];
  })();

  function mul(a, b) {
    return a && b ? EXP[LOG[a] + LOG[b]] : 0;
  }

  // Data codewords and EC codewords per version, level L, one block each.
  var DATA_CODEWORDS = [19, 34, 55, 80, 108, 136];
  var EC_CODEWORDS = [7, 10, 15, 20, 26, 36];
  var ALIGN_CENTRES = [[], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34]];

  function generatorPoly(degree) {
    var g = [1]; // lowest power first
    for (var i = 0; i < degree; i++) {
      var next = new Array(g.length + 1);
      for (var k = 0; k < next.length; k++) next[k] = 0;
      for (var j = 0; j < g.length; j++) {
        next[j] ^= mul(g[j], EXP[i]);
        next[j + 1] ^= g[j];
      }
      g = next;
    }
    return g.reverse(); // highest power first, leading coefficient 1
  }

  function errorCorrection(data, count) {
    var g = generatorPoly(count);
    var work = data.slice();
    for (var p = 0; p < count; p++) work.push(0);
    for (var i = 0; i < data.length; i++) {
      var lead = work[i];
      if (!lead) continue;
      for (var j = 1; j <= count; j++) work[i + j] ^= mul(g[j], lead);
    }
    return work.slice(data.length);
  }

  function utf8Bytes(text) {
    var encoded = unescape(encodeURIComponent(text));
    var bytes = [];
    for (var i = 0; i < encoded.length; i++) bytes.push(encoded.charCodeAt(i));
    return bytes;
  }

  function buildCodewords(bytes, version) {
    var capacity = DATA_CODEWORDS[version - 1];
    var bits = [];
    function push(value, length) {
      for (var i = length - 1; i >= 0; i--) bits.push((value >>> i) & 1);
    }

    push(4, 4);                 // byte mode
    push(bytes.length, 8);      // count indicator: 8 bits for versions 1-9
    for (var i = 0; i < bytes.length; i++) push(bytes[i], 8);

    var limit = capacity * 8;
    for (var t = 0; t < 4 && bits.length < limit; t++) bits.push(0);
    while (bits.length % 8) bits.push(0);

    var words = [];
    for (var b = 0; b < bits.length; b += 8) {
      var byte = 0;
      for (var k = 0; k < 8; k++) byte = (byte << 1) | bits[b + k];
      words.push(byte);
    }
    var pad = [0xec, 0x11];
    for (var p = 0; words.length < capacity; p++) words.push(pad[p % 2]);

    return words.concat(errorCorrection(words, EC_CODEWORDS[version - 1]));
  }

  function pickVersion(byteLength) {
    // Stops at 5. Version 6 at level L splits its data into two blocks,
    // which this encoder does not do, so anything over 106 bytes drew a
    // code no phone could read. Throwing hides the QR instead.
    for (var v = 1; v <= 5; v++) {
      if (4 + 8 + byteLength * 8 <= DATA_CODEWORDS[v - 1] * 8) return v;
    }
    return 0;
  }

  function maskAt(pattern, row, col) {
    switch (pattern) {
      case 0: return (row + col) % 2 === 0;
      case 1: return row % 2 === 0;
      case 2: return col % 3 === 0;
      case 3: return (row + col) % 3 === 0;
      case 4: return (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0;
      case 5: return ((row * col) % 2) + ((row * col) % 3) === 0;
      case 6: return ((((row * col) % 2) + ((row * col) % 3)) % 2) === 0;
      default: return ((((row + col) % 2) + ((row * col) % 3)) % 2) === 0;
    }
  }

  function buildMatrix(version) {
    var size = version * 4 + 17;
    var modules = [];
    var reserved = [];
    for (var r = 0; r < size; r++) {
      modules.push(new Array(size).fill(0));
      reserved.push(new Array(size).fill(false));
    }

    function set(row, col, dark) {
      modules[row][col] = dark ? 1 : 0;
      reserved[row][col] = true;
    }

    function finder(row, col) {
      for (var dr = -1; dr <= 7; dr++) {
        for (var dc = -1; dc <= 7; dc++) {
          var rr = row + dr;
          var cc = col + dc;
          if (rr < 0 || cc < 0 || rr >= size || cc >= size) continue;
          var edge = Math.max(Math.abs(dr - 3), Math.abs(dc - 3));
          set(rr, cc, edge !== 2 && edge <= 3);
        }
      }
    }

    finder(0, 0);
    finder(0, size - 7);
    finder(size - 7, 0);

    for (var t = 8; t < size - 8; t++) {
      set(6, t, t % 2 === 0);
      set(t, 6, t % 2 === 0);
    }

    var centres = ALIGN_CENTRES[version - 1];
    for (var a = 0; a < centres.length; a++) {
      for (var b = 0; b < centres.length; b++) {
        var cr = centres[a];
        var cc2 = centres[b];
        // Skip the three corners already occupied by finder patterns.
        if ((cr === 6 && cc2 === 6) ||
            (cr === 6 && cc2 === size - 7) ||
            (cr === size - 7 && cc2 === 6)) continue;
        for (var dr2 = -2; dr2 <= 2; dr2++) {
          for (var dc2 = -2; dc2 <= 2; dc2++) {
            set(cr + dr2, cc2 + dc2,
                Math.max(Math.abs(dr2), Math.abs(dc2)) !== 1);
          }
        }
      }
    }

    set(size - 8, 8, true); // always-dark module

    // Reserve the format-information strips.
    for (var f = 0; f < 9; f++) {
      if (!reserved[8][f]) reserved[8][f] = true;
      if (!reserved[f][8]) reserved[f][8] = true;
    }
    for (var g2 = 0; g2 < 8; g2++) {
      reserved[8][size - 1 - g2] = true;
      reserved[size - 1 - g2][8] = true;
    }

    return { size: size, modules: modules, reserved: reserved };
  }

  function placeData(grid, codewords) {
    var size = grid.size;
    var bitIndex = 0;
    var total = codewords.length * 8;

    function nextBit() {
      if (bitIndex >= total) return 0;
      var bit = (codewords[bitIndex >>> 3] >>> (7 - (bitIndex & 7))) & 1;
      bitIndex++;
      return bit;
    }

    var upward = true;
    for (var right = size - 1; right > 0; right -= 2) {
      if (right === 6) right--; // skip the vertical timing column
      for (var step = 0; step < size; step++) {
        var row = upward ? size - 1 - step : step;
        for (var c = 0; c < 2; c++) {
          var col = right - c;
          if (grid.reserved[row][col]) continue;
          grid.modules[row][col] = nextBit();
        }
      }
      upward = !upward;
    }
  }

  function penalty(modules, size) {
    var score = 0;

    // Rule 1 - runs of five or more of the same colour.
    for (var pass = 0; pass < 2; pass++) {
      for (var a = 0; a < size; a++) {
        var run = 1;
        for (var b = 1; b < size; b++) {
          var prev = pass ? modules[b - 1][a] : modules[a][b - 1];
          var cur = pass ? modules[b][a] : modules[a][b];
          if (cur === prev) {
            run++;
            if (run === 5) score += 3;
            else if (run > 5) score += 1;
          } else run = 1;
        }
      }
    }

    // Rule 2 - 2x2 blocks of one colour.
    for (var r = 0; r < size - 1; r++) {
      for (var c = 0; c < size - 1; c++) {
        var v = modules[r][c];
        if (v === modules[r][c + 1] && v === modules[r + 1][c] && v === modules[r + 1][c + 1]) {
          score += 3;
        }
      }
    }

    // Rule 3 - finder-like patterns.
    var a1 = [1, 0, 1, 1, 1, 0, 1, 0, 0, 0, 0];
    var a2 = [0, 0, 0, 0, 1, 0, 1, 1, 1, 0, 1];
    function matches(get, start, pattern) {
      for (var i = 0; i < 11; i++) if (get(start + i) !== pattern[i]) return false;
      return true;
    }
    for (var i2 = 0; i2 < size; i2++) {
      for (var j2 = 0; j2 <= size - 11; j2++) {
        var rowGet = function (k) { return modules[i2][k]; };
        var colGet = function (k) { return modules[k][i2]; };
        if (matches(rowGet, j2, a1) || matches(rowGet, j2, a2)) score += 40;
        if (matches(colGet, j2, a1) || matches(colGet, j2, a2)) score += 40;
      }
    }

    // Rule 4 - imbalance between dark and light.
    var dark = 0;
    for (var r2 = 0; r2 < size; r2++) {
      for (var c2 = 0; c2 < size; c2++) dark += modules[r2][c2];
    }
    var percent = (dark * 100) / (size * size);
    score += Math.floor(Math.abs(percent - 50) / 5) * 10;

    return score;
  }

  function writeFormat(grid, mask) {
    var size = grid.size;
    var value = (0x01 << 3) | mask; // 0b01 = error correction level L
    var rem = value;
    for (var i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537);
    var bits = ((value << 10) | (rem & 0x3ff)) ^ 0x5412;

    // Format bits go out most-significant first: the first position holds
    // bit 14, not bit 0. Verified against a known-good code.
    function bitAt(index) { return (bits >>> (14 - index)) & 1; }

    for (var k = 0; k <= 5; k++) grid.modules[8][k] = bitAt(k);
    grid.modules[8][7] = bitAt(6);
    grid.modules[8][8] = bitAt(7);
    grid.modules[7][8] = bitAt(8);
    for (var m = 9; m <= 14; m++) grid.modules[14 - m][8] = bitAt(m);

    // Second copy: seven bits climbing the left edge, eight along the top
    // right. The eighth position in the vertical strip is the always-dark
    // module, not a format bit - getting that boundary wrong shifts every
    // remaining bit and no scanner will read the result.
    for (var n = 0; n <= 6; n++) grid.modules[size - 1 - n][8] = bitAt(n);
    for (var p = 7; p <= 14; p++) grid.modules[8][size - 15 + p] = bitAt(p);

    grid.modules[size - 8][8] = 1; // always dark
  }

  function encode(text) {
    var bytes = utf8Bytes(text);
    var version = pickVersion(bytes.length);
    if (!version) throw new Error('text too long for this encoder');

    var codewords = buildCodewords(bytes, version);
    var grid = buildMatrix(version);
    placeData(grid, codewords);

    var size = grid.size;
    var base = grid.modules.map(function (row) { return row.slice(); });
    var best = null;

    for (var mask = 0; mask < 8; mask++) {
      var candidate = base.map(function (row) { return row.slice(); });
      for (var r = 0; r < size; r++) {
        for (var c = 0; c < size; c++) {
          if (!grid.reserved[r][c] && maskAt(mask, r, c)) candidate[r][c] ^= 1;
        }
      }
      var masked = { size: size, modules: candidate, reserved: grid.reserved };
      writeFormat(masked, mask);
      var score = penalty(candidate, size);
      if (!best || score < best.score) best = { score: score, modules: candidate };
    }

    return { size: size, modules: best.modules };
  }

  // Rendered as one SVG path so it stays crisp at any size and costs one node.
  function toSvg(text, quietZone) {
    var qr = encode(text);
    var quiet = quietZone === undefined ? 4 : quietZone;
    var span = qr.size + quiet * 2;
    var path = '';
    for (var r = 0; r < qr.size; r++) {
      for (var c = 0; c < qr.size; c++) {
        if (qr.modules[r][c]) path += 'M' + (c + quiet) + ' ' + (r + quiet) + 'h1v1h-1z';
      }
    }
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + span + ' ' + span +
           '" shape-rendering="crispEdges" width="100%" height="100%">' +
           '<rect width="' + span + '" height="' + span + '" fill="#fff"/>' +
           '<path d="' + path + '" fill="#000"/></svg>';
  }

  return toSvg;
})();

function neferArInit(root) {
  if (root.getAttribute('data-nefer-ar-ready')) return;
  root.setAttribute('data-nefer-ar-ready', '1');

  var productId = root.getAttribute('data-ar-product-id') || '';
  var productHandle = root.getAttribute('data-ar-handle') || '';
  var productTitle = root.getAttribute('data-ar-title') || '';
  var dimensionsText = root.getAttribute('data-ar-dimensions') || '';

  var trigger = root.querySelector('[data-nefer-ar-link]');

  // Apple's own feature test. True on iOS/iPadOS Safari, false everywhere
  // else - including Chrome on iOS, which cannot open AR Quick Look.
  var probe = document.createElement('a');
  var hasAR = !!(probe.relList && probe.relList.supports && probe.relList.supports('ar'));

  function track(name) {
    if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) {
      Shopify.analytics.publish(name, {
        product_id: productId,
        handle: productHandle
      });
    }
  }

  // Arrivals from the QR code carry ?ar=1. Take them straight to the
  // button and make it the obvious thing to press, so the trip is scan,
  // one tap, camera - with the price and Add to Cart still behind it.
  //
  // Deliberately not opening AR automatically: Safari wants a real
  // finger-press first, and when it refuses it follows the link instead,
  // stranding the shopper on a bare file preview with nothing to buy from.
  if (/[?&]ar=1(?:&|$)/.test(location.search)) {
    // The ring needs no layout, so apply it straight away.
    trigger.style.transition = 'box-shadow .4s ease';
    trigger.style.boxShadow = '0 0 0 4px rgba(79, 91, 58, .22)';
    setTimeout(function () { trigger.style.boxShadow = 'none'; }, 2600);

    // The scroll waits for the page to settle - product images loading
    // above the button would otherwise shift it out from under us.
    function bringIntoView() {
      var stillness = window.matchMedia &&
                      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      trigger.scrollIntoView({ block: 'center', behavior: stillness ? 'auto' : 'smooth' });
    }
    if (document.readyState === 'complete') setTimeout(bringIntoView, 0);
    else window.addEventListener('load', bringIntoView);

    track('nefer_ar_arrived_from_qr');
  }

  if (hasAR) {
    // Nothing is intercepted: the link itself opens AR in a single tap, which
    // is the whole reason the button is a link and not a button.
    trigger.addEventListener('click', function () { track('nefer_ar_opened'); });
    return;
  }

  // ---- Android -------------------------------------------------------

  // Instructions, Android only. The iPhone returned above and never reaches
  // this code, which is deliberate: intercepting its click is what stopped
  // the camera opening last time.
  var stepTips = root.querySelector('[data-nefer-ar-step="tips"]');
  var launcher = root.querySelector('[data-nefer-ar-launch]');
  var skipBox = root.querySelector('[data-nefer-ar-skip-tips]');
  var TIPS_SEEN = 'nefer-ar-tips-seen';

  function tipsWanted() {
    try {
      return window.localStorage.getItem(TIPS_SEEN) !== '1';
    } catch (error) {
      return true; // private browsing: show them, they cost one tap
    }
  }

  function showTips() {
    stepTips.hidden = false;
    stepPick.hidden = true;
    stepPlace.hidden = true;
    stepXr.hidden = true;
  }

  launcher.addEventListener('click', function () {
    if (skipBox && skipBox.checked) {
      try { window.localStorage.setItem(TIPS_SEEN, '1'); } catch (error) {}
    }
    track('nefer_ar_opened_android');
    if (viewer && viewer.loaded && viewer.canActivateAR) {
      viewer.activateAR();
    } else {
      showAndroid(); // not ready yet - fall back to the 3D panel
    }
  });

  var androidModel = (root.getAttribute('data-ar-glb') || '');
  var viewerScript = (root.getAttribute('data-ar-viewer-src') || '');
  var androidReady = false;
  var androidCheck = null;   // the pending question
  var androidAnswer = null;  // null until it comes back, then true/false

  // Asking whether the phone can do AR is asynchronous, and on some
  // handsets it takes a moment. The button must therefore wait for the
  // answer rather than read a flag that may not be set yet - tapping
  // quickly used to fall through to the photo view on a capable phone.
  if (androidModel && navigator.xr && navigator.xr.isSessionSupported) {
    androidCheck = navigator.xr.isSessionSupported('immersive-ar')
      .then(function (supported) {
        androidAnswer = !!supported;
        androidReady = androidAnswer;
        report('Phone can do AR', androidAnswer ? 'yes' : 'NO - this is why the photo view appears');
        return androidAnswer;
      })
      .catch(function (error) {
        androidAnswer = false;
        report('Phone can do AR', 'check failed: ' + error);
        return false;
      });
  }

  // Add ?ardebug=1 to any product URL to see, on the device itself, why a
  // given phone took the route it did. Costs nothing when not asked for.
  function report(label, value) {
    var panel = document.getElementById('nefer-ar-debug');
    if (!panel) return;
    panel.textContent += label + ': ' + value + '\n';
  }

  if (/[?&]ardebug=1/.test(location.search)) {
    var panel = document.createElement('pre');
    panel.id = 'nefer-ar-debug';
    panel.style.cssText = 'margin-top:12px;padding:12px;background:#111;color:#0f0;' +
                          'font:11px/1.5 monospace;white-space:pre-wrap;word-break:break-all;' +
                          'border-radius:4px;';
    root.appendChild(panel);

    report('iPhone AR available', hasAR ? 'yes' : 'no');
    report('Android 3D file', androidModel || 'MISSING - the block or the file is not in place');
    report('Browser supports WebXR', navigator.xr ? 'yes' : 'NO - use Chrome on Android');
    report('Secure connection', location.protocol === 'https:' ? 'yes' : 'NO - AR needs https');
    report('Browser', navigator.userAgent);

    // model-viewer draws with WebGL. Where that is unavailable - blocked,
    // or on a device with no hardware acceleration - the preview can stall
    // silently rather than raising an error.
    var probeCanvas = document.createElement('canvas');
    var gl = probeCanvas.getContext('webgl2') || probeCanvas.getContext('webgl');
    report('3D graphics (WebGL)', gl ? 'available' : 'NOT AVAILABLE - this alone stops the preview');

    if (androidModel && window.fetch) {
      fetch(androidModel, { method: 'HEAD' }).then(function (response) {
        report('3D file reachable', response.status + ' ' +
               (response.headers.get('content-type') || ''));
      }).catch(function (error) {
        report('3D file reachable', 'FAILED - ' + error.message);
      });
    }

    var probeScript = document.createElement('script');
    probeScript.type = 'module';
    probeScript.src = viewerScript;
    probeScript.onload = function () { report('3D library', 'loaded'); };
    probeScript.onerror = function () {
      report('3D library', 'FAILED to load - upload model-viewer.min.js to theme Assets');
    };
    document.head.appendChild(probeScript);
  }

  // ---- photo fallback -------------------------------------------------

  // Height first, then width - the order Nefer's dimensions field uses.
  var match = dimensionsText.match(/(\d+(?:[.,]\d+)?)\s*[x\u00D7]\s*(\d+(?:[.,]\d+)?)/i);
  if (!match) return;
  var artHeightCm = parseFloat(match[1].replace(',', '.'));
  var artWidthCm = parseFloat(match[2].replace(',', '.'));

  var dialog = root.querySelector('[data-nefer-ar-dialog]');
  var stepPick = root.querySelector('[data-nefer-ar-step="pick"]');
  var stepPlace = root.querySelector('[data-nefer-ar-step="place"]');
  var fileInput = root.querySelector('[data-nefer-ar-file]');
  var stage = root.querySelector('[data-nefer-ar-stage]');
  var room = root.querySelector('[data-nefer-ar-room]');
  var art = root.querySelector('[data-nefer-ar-art]');
  var wall = root.querySelector('[data-nefer-ar-wall]');
  var wallValue = root.querySelector('[data-nefer-ar-wall-value]');

  // Position is kept as a fraction of the photo, so it survives the panel
  // being resized and maps straight onto the full-resolution download.
  var posX = 0.5;
  var posY = 0.38;

  var drawAttempts = 0;

  function draw() {
    var stageWidth = stage.clientWidth;

    // A dialog can report zero width for a frame or two after opening.
    // Giving up there would leave the photo and the painting sized to
    // nothing, which looks exactly like a feature that did not work - so
    // wait for layout instead, for up to a second.
    if (!stageWidth) {
      if (drawAttempts++ < 60) requestAnimationFrame(draw);
      return;
    }
    drawAttempts = 0;

    var wallCm = parseFloat(wall.value);
    var widthPx = stageWidth * (artWidthCm / wallCm);
    var heightPx = widthPx * (artHeightCm / artWidthCm);

    art.style.width = widthPx + 'px';
    art.style.height = heightPx + 'px';
    art.style.left = (posX * stageWidth - widthPx / 2) + 'px';
    art.style.top = (posY * stage.clientHeight - heightPx / 2) + 'px';

    wallValue.textContent = ' ' + Math.round(wallCm) + ' cm';
  }

  var qrHolder = root.querySelector('[data-nefer-ar-qr]');

  function drawQr() {
    if (qrHolder.firstChild) return;
    try {
      // The painting's plain address, never the live URL: arriving from
      // search or a collection adds tracking text (_pos, _psq, /collections/
      // ...) that pushed the code past what the encoder can draw, and phones
      // could not read it. Shopify.routes.root keeps the shopper's language
      // (/fr/, /de/). ?ar=1 tells the phone to scroll to the button.
      if (!productHandle) throw new Error('no handle to link to');
      var localeRoot = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      var target = location.origin + localeRoot + 'products/' +
                   encodeURIComponent(productHandle) + '?ar=1';
      qrHolder.innerHTML = neferQrSvg(target);
    } catch (error) {
      qrHolder.parentNode.hidden = true; // no QR is better than a broken one
    }
  }

  var stepXr = root.querySelector('[data-nefer-ar-step="xr"]');
  var holder = root.querySelector('[data-nefer-ar-holder]');
  var viewerBox = root.querySelector('[data-nefer-ar-viewer]');
  var spinner = root.querySelector('[data-nefer-ar-spinner]');
  var xrCta = root.querySelector('[data-nefer-ar-xr-cta]');
  var viewer = null;
  var viewerRequested = false;

  function showPhotoStep(onAPhone) {
    androidReady = false;
    stepXr.hidden = true;
    stepPick.hidden = false;
    // "Open this on your phone" is nonsense when you already are on one -
    // but a desktop reaching here must keep its QR code.
    if (onAPhone && qrHolder.parentNode) qrHolder.parentNode.hidden = true;
    drawQr();
  }

  // Used as an event handler, so it must take no meaningful argument:
  // anything going wrong with the 3D viewer lands the shopper on the
  // photo view rather than a dead panel.
  function fallBackToPhoto() {
    showPhotoStep(true);
  }

  function markViewerReady() {
    if (!viewer || !viewer.loaded) return false;
    // If the panel is open, the model belongs in it - this covers the
    // case where the shopper tapped before it had finished preparing, so
    // the viewer was still waiting off-screen.
    if (!stepXr.hidden && viewer.parentNode !== viewerBox) {
      viewerBox.appendChild(viewer);
    }
    if (spinner) {
      spinner.remove();
      spinner = null;
    }
    viewerBox.style.display = 'block';
    xrCta.disabled = false;
    return true;
  }

  function showAndroid() {
    stepPick.hidden = true;
    stepPlace.hidden = true;
    stepXr.hidden = false;
    // Bring the prepared viewer in from off-screen.
    if (viewer && viewer.parentNode !== viewerBox) {
      viewerBox.appendChild(viewer);
      markViewerReady();
    }
    ensureViewer();
  }

  function ensureViewer() {
    if (viewerRequested) return;
    viewerRequested = true;

    // Loaded only here: iPhone and desktop never fetch it.
    var script = document.createElement('script');
    script.type = 'module';
    script.src = viewerScript;
    script.onerror = fallBackToPhoto;
    document.head.appendChild(script);

    if (!window.customElements) return fallBackToPhoto();

    // Wait for the library to register its element, but never for ever.
    // Without this cap, a library that loads yet fails to run leaves the
    // shopper watching a spinner with nothing behind it.
    Promise.race([
      customElements.whenDefined('model-viewer').then(function () { return 'ready'; }),
      new Promise(function (resolve) {
        setTimeout(function () { resolve('timeout'); }, 8000);
      })
    ]).then(function (outcome) {
      if (outcome !== 'ready') {
        report('3D component', 'NEVER REGISTERED - the library loaded but did not run');
        return fallBackToPhoto();
      }
      report('3D component', 'registered');
      buildViewer();
    });
  }

  function buildViewer() {
    {
      viewer = document.createElement('model-viewer');
      viewer.setAttribute('src', androidModel);
      viewer.setAttribute('alt', productTitle);
      viewer.setAttribute('ar', '');
      // WebXR - the viewer that runs inside the browser.
      //
      // Google's Scene Viewer was tried here, because it aligns to the full
      // wall surface rather than only spinning the model about the vertical
      // axis. It launches through an Android intent into Google's own app,
      // and on at least one real handset that opened a blank screen and
      // bounced straight back to the product page. A working preview with a
      // known quirk beats a broken one, so WebXR is the default again.
      //
      // ?arviewer=scene-viewer forces the other route, for retrying it on a
      // different phone.
      viewer.setAttribute(
        'ar-modes',
        /[?&]arviewer=scene-viewer(?:&|$)/.test(location.search) ? 'scene-viewer webxr' : 'webxr'
      );
      viewer.setAttribute('ar-placement', 'wall');
      viewer.setAttribute('ar-scale', 'fixed');
      // Dead-on view of an upright painting. Set explicitly rather than
      // left to the default, so the preview cannot drift with a library
      // update, and fixed deliberately: this is a preview on the way to
      // the camera, not a model to be spun around.
      viewer.setAttribute('camera-orbit', '0deg 90deg auto');
      viewer.setAttribute('interaction-prompt', 'none');
      viewer.setAttribute('disable-zoom', '');
      viewer.setAttribute('shadow-intensity', '0');
      // Load at once. The default waits for the element to be judged
      // visible, and inside a panel that has only just opened that can
      // leave the shopper watching a spinner that never resolves.
      viewer.setAttribute('loading', 'eager');
      viewer.setAttribute('reveal', 'auto');
      viewer.style.width = '100%';
      viewer.style.height = '100%';

      var furthest = 0;

      viewer.addEventListener('load', markViewerReady);
      viewer.addEventListener('progress', function (event) {
        if (event.detail && typeof event.detail.totalProgress === 'number') {
          furthest = Math.max(furthest, Math.round(event.detail.totalProgress * 100));
        }
        markViewerReady();
      });
      // The viewer announces when the AR session starts and ends. Without
      // listening, leaving AR drops the shopper back onto whatever panel was
      // open behind it - a half-state with buttons that lead nowhere.
      var wasPresenting = false;
      viewer.addEventListener('ar-status', function (event) {
        var status = event.detail && event.detail.status;
        if (status === 'session-started') wasPresenting = true;
        if (status === 'failed') {
          report('AR session', 'failed to start');
          showAndroid();
          openPanel();
          return;
        }
        if (status === 'not-presenting' && wasPresenting) {
          wasPresenting = false;
          // Back to the product page, where the price and Add to Cart are.
          if (dialog.open) {
            if (dialog.close) dialog.close();
            else { dialog.removeAttribute('open'); reset(); }
          }
        }
      });

      viewer.addEventListener('error', function (event) {
        var why = event.detail && event.detail.type ? event.detail.type : 'unknown';
        report('3D model', 'ERROR (' + why + ')');
        fallBackToPhoto();
      });

      // Starts off-screen so it is ready before the shopper taps; moved
      // into the panel only if the panel is actually needed.
      holder.appendChild(viewer);

      // The "load" event is not dependable - in some builds it only fires
      // once the page is actually being painted. The loaded property is,
      // so poll it as well rather than risk a spinner that never resolves.
      var waited = 0;
      var poll = setInterval(function () {
        waited += 400;
        if (markViewerReady()) {
          clearInterval(poll);
          report('3D model', 'loaded');
          return;
        }
        if (waited > 20000) {
          clearInterval(poll);
          report('3D model', 'gave up - reached ' + furthest + '% of the file');
          fallBackToPhoto();
        }
      }, 400);
    }
  }

  xrCta.addEventListener('click', function () {
    if (!viewer || !viewer.activateAR) return;
    track('nefer_ar_opened_android');
    viewer.activateAR();
  });

  // Start preparing once the button is on screen - a good sign the shopper
  // is at the buying end of the page - rather than on page load, so a
  // browsing visitor never pays for the download.
  function preloadWhenSeen() {
    if (!window.IntersectionObserver) return ensureViewer();
    var watcher = new IntersectionObserver(function (entries) {
      if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
      watcher.disconnect();
      ensureViewer();
    }, { rootMargin: '400px' });
    watcher.observe(trigger);
  }

  if (androidCheck) {
    androidCheck.then(function (supported) {
      if (supported) preloadWhenSeen();
    });
  }

  function openPanel() {
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  }

  function openDialog(event) {
    event.preventDefault();
    track('nefer_ar_opened');

    // One tap into the camera, matching the iPhone - possible only
    // because the model was made ready in advance. If anything is not
    // quite ready, fall back to the panel rather than doing nothing.
    if (androidAnswer === true && tipsWanted()) {
      showTips();
      openPanel();
      return;
    }

    if (androidAnswer === true && viewer && viewer.loaded && viewer.canActivateAR) {
      track('nefer_ar_opened_android');
      try {
        var launching = viewer.activateAR();
        if (launching && launching.catch) {
          launching.catch(function () { showAndroid(); openPanel(); });
        }
        return;
      } catch (error) {
        // fall through to the panel
      }
    }

    if (androidCheck) {
      if (androidAnswer === true) {
        showAndroid();
      } else if (androidAnswer === false) {
        showPhotoStep(false);
      } else {
        // Answer still in flight: hold on the 3D panel's spinner and
        // branch the moment it lands, rather than guessing wrong.
        stepPick.hidden = true;
        stepPlace.hidden = true;
        stepXr.hidden = false;
        androidCheck.then(function (supported) {
          if (supported) showAndroid();
          else showPhotoStep(false);
        });
      }
      openPanel();
      return;
    }
    drawQr();
    openPanel();
    // Re-measure now the panel is on screen and has a real width.
    if (!stepPlace.hidden) draw();
  }

  trigger.addEventListener('click', openDialog);

  root.querySelector('[data-nefer-ar-close]').addEventListener('click', function () {
    if (dialog.close) {
      dialog.close(); // fires the close event, which resets
    } else {
      dialog.removeAttribute('open');
      reset();
    }
  });

  var errorNote = root.querySelector('[data-nefer-ar-error]');

  fileInput.addEventListener('change', function () {
    var file = fileInput.files && fileInput.files[0];
    if (!file) return;

    errorNote.hidden = true;
    var objectUrl = URL.createObjectURL(file);

    room.onload = function () {
      URL.revokeObjectURL(objectUrl);
      stepPick.hidden = true;
      stepPlace.hidden = false;
      posX = 0.5;
      posY = 0.38;
      draw();
    };

    room.onerror = function () {
      // Almost always an iPhone HEIC opened in a browser that cannot decode
      // it. Say so, rather than leaving the shopper staring at nothing.
      URL.revokeObjectURL(objectUrl);
      fileInput.value = '';
      errorNote.hidden = false;
    };

    room.src = objectUrl;
  });

  // Back to the empty state, with the shopper's photo dropped. Called both
  // by "use another photo" and whenever the panel closes - a photo of
  // someone's living room has no business staying in the page after they
  // have finished with it.
  function reset() {
    room.onload = null;
    room.onerror = null;
    room.removeAttribute('src');
    fileInput.value = '';
    stepPlace.hidden = true;
    errorNote.hidden = true;
    // Reopen where this device belongs: the 3D viewer on Android, the
    // photo picker everywhere else.
    stepTips.hidden = true;
    stepPick.hidden = androidReady;
    stepXr.hidden = !androidReady;
  }

  root.querySelector('[data-nefer-ar-reset]').addEventListener('click', reset);

  // Watch the panel's own state rather than listening for its "close"
  // event: some browsers never dispatch that event, which would leave the
  // shopper's photo sitting in the page after they had closed the panel.
  // This catches every route out - the close button, Esc, anything.
  if (window.MutationObserver) {
    new MutationObserver(function () {
      if (!dialog.open) reset();
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
  } else {
    dialog.addEventListener('close', reset);
  }

  wall.addEventListener('input', draw);
  window.addEventListener('resize', draw);

  // ---- dragging -------------------------------------------------------

  var dragging = false;

  art.addEventListener('pointerdown', function (event) {
    dragging = true;
    art.setPointerCapture(event.pointerId);
    art.style.cursor = 'grabbing';
  });

  art.addEventListener('pointermove', function (event) {
    if (!dragging) return;
    var box = stage.getBoundingClientRect();
    posX = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width));
    posY = Math.min(1, Math.max(0, (event.clientY - box.top) / box.height));
    draw();
  });

  art.addEventListener('pointerup', function () {
    dragging = false;
    art.style.cursor = 'grab';
  });

  // ---- save -----------------------------------------------------------

  var saveButton = root.querySelector('[data-nefer-ar-save]');

  saveButton.addEventListener('click', function () {
    try {
      exportImage();
    } catch (error) {
      // Only reachable if the artwork ever stops being served with CORS
      // headers. Losing the download is survivable; the preview is the
      // part that sells. Hide the button rather than fail on every click.
      saveButton.hidden = true;
    }
  });

  function exportImage() {
    var canvas = document.createElement('canvas');
    canvas.width = room.naturalWidth;
    canvas.height = room.naturalHeight;
    var ctx = canvas.getContext('2d');
    ctx.drawImage(room, 0, 0);

    var widthPx = canvas.width * (artWidthCm / parseFloat(wall.value));
    var heightPx = widthPx * (artHeightCm / artWidthCm);
    var x = posX * canvas.width - widthPx / 2;
    var y = posY * canvas.height - heightPx / 2;

    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,.34)';
    ctx.shadowBlur = canvas.width * 0.02;
    ctx.shadowOffsetY = canvas.width * 0.008;
    ctx.drawImage(art, x, y, widthPx, heightPx);
    ctx.restore();

    canvas.toBlob(function (blob) {
      var url = URL.createObjectURL(blob);
      var link = document.createElement('a');
      link.href = url;
      link.download = productHandle + '-in-my-room.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      track('nefer_ar_saved');
    }, 'image/jpeg', 0.92);
  }
}

function neferArInitAll() {
  var blocks = document.querySelectorAll('[data-nefer-ar]');
  for (var i = 0; i < blocks.length; i++) neferArInit(blocks[i]);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', neferArInitAll);
} else {
  neferArInitAll();
}
